import { useEffect, useState, useMemo, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom/cjs/react-router-dom.min';

import { useWebSocketContext } from 'context/WebSocketConnectionContext';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { useNavbarContext } from 'context/NavbarContext';
import { listTextMessages } from 'services/textMessageService';
import { getLocalStorageParseItem, removeLocalStorageItem } from 'utils/localStorageUtils';
import { reorderList } from 'utils/utils';
import { toastConstant } from 'constants/toastmessage';
import { useToast } from 'context/ToastContext';
import useEncryptedURLState from 'hooks/useEncryptedURLState';

export default function useTextMessage(leadDetails, caseDetails, isFromCase, isFromLead, contactId, phoneNumber) {
  const userContext = useUserDetailsContext();
  const websocket = useWebSocketContext();
  const location = useLocation();
  const history = useHistory();
  const { addToast } = useToast();
  const navbarContext = useNavbarContext();
  const loadNotificationCount = navbarContext?.notificationData?.loadNotificationCount;
  const loadNotificationCountRef = useRef(loadNotificationCount);
  const prevSelectedPracticeAreasRef = useRef([]);

  // Update ref when loadNotificationCount changes
  useEffect(() => {
    loadNotificationCountRef.current = loadNotificationCount;
  }, [loadNotificationCount]);

  const [messageThreads, setMessageThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState('');
  const [selectedContact, setSelectedContact] = useState(undefined);
  const [newThreadActive, setNewThreadActive] = useState(false);
  const [activeIndex, setActiveIndex] = useEncryptedURLState('tab', 1, { encrypt: false }); // State for active tab
  const prevActiveIndexRef = useRef(activeIndex);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPracticeAreas, setSelectedPracticeAreas] = useState([]);
  const [pageCount, setPageCount] = useState(1); // Start at 2 because page 1 is initial load
  const [isLastPage, setIsLastPage] = useState(false);

  // Calculate dynamic limit based on screen height
  // Estimated: ~75px per thread item, reserve ~300px for headers/footers, multiply by 2 for buffer to enable scrolling
  const dynamicLimit = useMemo(() => {
    const estimatedItemHeight = 75;
    const reservedSpace = 300;
    const availableHeight = window.innerHeight - reservedSpace;
    const visibleItems = Math.max(1, Math.floor(availableHeight / estimatedItemHeight));
    // Multiply by 2 to ensure enough items for scrolling and next page loading
    const calculatedLimit = Math.ceil(visibleItems * 2);
    // Ensure minimum of 20 and maximum reasonable limit
    return Math.max(15, Math.min(calculatedLimit, 100));
  }, [window.innerHeight]);

  //To do: update websocket logic
  useEffect(() => {
    //Update Side thread list on websocket
    if (websocket?.textMessageData?.body?.from) {
      const isInboundMessage =
        websocket?.textMessageData?.notification_type === 'inbound_text_message' ||
        websocket?.textMessageData?.notification_type === 'text_message';

      setMessageThreads((pre) => {
        const threadIndex = pre?.findIndex(
          (item) => item?.from_ === websocket?.textMessageData?.body?.from || item?.to === websocket?.textMessageData.body?.from
        );
        if (threadIndex > -1) {
          let updatedList = pre.map((item) => {
            if (item?.from_ === websocket?.textMessageData?.body?.from || item?.to === websocket?.textMessageData.body?.from) {
              return {
                ...item,
                last_message: websocket?.textMessageData?.body?.message_body,
                last_author: isInboundMessage ? 'client' : websocket?.textMessageData?.body?.client_name,
                last_updated: Date.now(),
                unread_reply_count: 1,
                is_read: false, // Mark as unread for new messages
              }; // Create a new object with updated name
            }
            return item; // Return the original item if ID doesn't match
          });
          return reorderList(updatedList, threadIndex, 0);
        } else {
          // Thread not found in current list
          // This can happen when a new message comes to a previously read thread while viewing Unread tab
          // We'll let the next useEffect handle the refresh when activeIndex changes
          return pre;
        }
      });

      // Refresh notification count when a new inbound message arrives
      if (isInboundMessage && loadNotificationCount) {
        // Add a small delay to ensure backend has processed the message
        setTimeout(() => {
          const practiceAreas = selectedPracticeAreas?.length > 0 ? selectedPracticeAreas : [];
          loadNotificationCount(practiceAreas);
        }, 500); // 500ms delay
      }
    }

    // delivery status Update Logic
    if (websocket?.textMessageData?.body?.SmsSid) {
      setMessageThreads((pre) => {
        return pre.map((item) => {
          if (item?.message_id === websocket?.textMessageData?.body?.SmsSid) {
            return { ...item, last_message_status: websocket?.textMessageData?.body?.SmsStatus }; // Create a new object with updated name
          }
          return item; // Return the original item if ID doesn't match
        });
      });
    }
  }, [websocket, loadNotificationCount, selectedPracticeAreas]);

  useEffect(() => {
    if (contactId && phoneNumber && !isFromCase && !isFromLead) {
      setActiveIndex(0);
      fetchTextMessages(contactId, false, '', { practice_area_ids: [] });
    }
  }, [contactId, phoneNumber, isFromCase, isFromLead]);

  useEffect(() => {
    if (contactId && phoneNumber && !isFromCase && !isFromLead) {
      return;
    }

    if (selectedContact?.contact_id || selectedContact === undefined || selectedContact === '' || selectedPracticeAreas) {
      // Build data object with appropriate filters
      let data = {};
      if (!isFromCase && !isFromLead) {
        // For global text messages, include practice area filter
        data = { practice_area_ids: selectedPracticeAreas };
      } else {
        // For case/lead specific text messages, include case_ids/lead_ids filter
        data = { practice_area_ids: [] };
        if (isFromCase && caseDetails?.case_id) {
          data.case_ids = [caseDetails.case_id];
        }
        if (isFromLead && leadDetails?.case_id) {
          data.case_ids = [leadDetails.case_id];
        }
      }

      // Add contact_ids to data object when a contact is selected
      if (selectedContact?.contact_id) {
        data.contact_ids = [selectedContact.contact_id];
      }

      fetchTextMessages(selectedContact?.contact_id, false, activeIndex === 0 ? '' : false, data);

      // Refresh notification count ONLY when practice areas or activeIndex actually change (not on every dependency change)
      const practiceAreasChanged = JSON.stringify(prevSelectedPracticeAreasRef.current) !== JSON.stringify(selectedPracticeAreas);
      const activeIndexChanged = prevActiveIndexRef.current !== activeIndex;

      // Only call loadNotificationCount if practice areas or activeIndex actually changed
      // This prevents duplicate calls when other dependencies change (like caseDetails, leadDetails, etc.)
      let timeoutId = null;
      if (loadNotificationCountRef.current && !isFromCase && !isFromLead && (practiceAreasChanged || activeIndexChanged)) {
        // Use a small delay to debounce and prevent rapid successive calls
        timeoutId = setTimeout(() => {
          loadNotificationCountRef.current(selectedPracticeAreas?.length > 0 ? selectedPracticeAreas : []);
        }, 100);
      }

      // Update refs to track current state
      prevSelectedPracticeAreasRef.current = selectedPracticeAreas;
      prevActiveIndexRef.current = activeIndex;

      // Cleanup timeout on unmount or if effect runs again before timeout
      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }

    // Return empty cleanup function if condition above is false
    return () => {};
  }, [
    selectedContact,
    activeIndex,
    selectedPracticeAreas,
    isFromCase,
    isFromLead,
    contactId,
    phoneNumber,
    caseDetails,
    leadDetails,
    // Note: loadNotificationCount removed from dependencies to prevent unnecessary re-runs
    // It's tracked via loadNotificationCountRef instead
  ]);

  useEffect(() => {
    // Check if selectedThread is a real thread from API (has sk or id) vs created from contact search
    const isRealThread = selectedThread?.sk || selectedThread?.id;

    // Priority 1: If selectedThread is a real thread from API, use its is_active value directly
    // This handles the case when thread is clicked in Unread tab and removed from messageThreads
    if (isRealThread) {
      const isActive = selectedThread?.is_active !== false && selectedThread?.is_active !== 'false';
      setNewThreadActive(isActive);
      return;
    }

    // Priority 2: For created threads (from contact search), check messageThreads for this contact
    if (messageThreads?.length > 0) {
      let currentContactThreads = messageThreads?.filter((t) => t.contact_id === selectedThread?.contact_id);
      if (currentContactThreads?.length > 0) {
        // Contact has threads in the list - use their is_active status
        let checkThreadActive = currentContactThreads.some((c) => c.is_active === true);
        setNewThreadActive(checkThreadActive);
      } else {
        // Contact has no threads in current list (filtered by tab)
        // Use the created thread's is_active value:
        // - true (default for new search) → show send area
        // - false (preserved from inactive thread) → show button
        const isActive = selectedThread?.is_active !== false && selectedThread?.is_active !== 'false';
        setNewThreadActive(isActive);
      }
    } else {
      // No threads at all - use created thread's is_active value
      const isActive = selectedThread?.is_active !== false && selectedThread?.is_active !== 'false';
      setNewThreadActive(isActive);
    }
  }, [selectedThread, messageThreads]);

  const fetchTextMessages = async (contactId = false, isNewTread = false, is_read = activeIndex === 0 ? '' : false, data = {}) => {
    try {
      // Reset pagination state for initial fetch
      setPageCount(2); // Next page will be 2
      setIsLastPage(false);

      const hasUrlParams = contactId && phoneNumber && !isFromCase && !isFromLead;

      // Ensure contact_ids is added to data object when contactId is provided (except for Global Search redirect)
      if (!hasUrlParams && contactId && (!data.contact_ids || !Array.isArray(data.contact_ids) || data.contact_ids.length === 0)) {
        data = { ...data, contact_ids: [contactId] };
      }

      if (hasUrlParams) {
        // Don't pass contact_id to API when redirecting from Global Search
        const threadsResponse = await listTextMessages(
          leadDetails?.case_id || caseDetails?.case_id,
          null, // Don't pass contact_id when from redirection
          null,
          dynamicLimit,
          '',
          null,
          'forward',
          false,
          1, // Always start with page 1
          data
        );

        if (threadsResponse?.data?.text_message_threads?.length > 0) {
          setIsLastPage(threadsResponse?.data?.is_last_page || false);

          // Set all threads first
          setMessageThreads(threadsResponse.data.text_message_threads);

          // Then find and select the matching thread
          const matchingThread = threadsResponse.data.text_message_threads.find(
            (thread) => thread.contact_id === contactId && (thread.to === phoneNumber || thread.from_ === phoneNumber)
          );

          if (matchingThread) {
            matchingThread.is_read = true;
            handleSelectThread(matchingThread);

            if (matchingThread?.contact_id) {
              const searchParams = new URLSearchParams(location.search);
              searchParams.set('contactId', matchingThread?.contact_id);
              history.replace({
                pathname: location.pathname,
                search: `?${searchParams.toString()}`,
              });
            }
          }
        } else {
          setMessageThreads([]);
          setIsLastPage(true);
        }
      } else {
        const response = await listTextMessages(
          leadDetails?.case_id || caseDetails?.case_id,
          contactId,
          null,
          dynamicLimit,
          is_read,
          null,
          'forward',
          false,
          1, // Always start with page 1
          data
        );

        if (response?.data?.text_message_threads?.length) {
          setIsLastPage(response?.data?.is_last_page || false);

          let redirectedData = location?.state?.text_message
            ? location?.state?.text_message
            : getLocalStorageParseItem('stateData')?.text_message;
          let filteredData = response?.data?.text_message_threads?.filter((data) =>
            data?.contact_id === redirectedData?.contact_id && data?.to === redirectedData?.to ? redirectedData?.to : redirectedData?.To
          );
          if (isNewTread) {
            response.data.text_message_threads[0].is_read = true;
            handleSelectThread(response.data.text_message_threads?.[0]);

            if (response.data.text_message_threads?.[0]?.contact_id) {
              const searchParams = new URLSearchParams(location.search);
              searchParams.set('contactId', response.data.text_message_threads?.[0]?.contact_id);
              history.replace({
                pathname: location.pathname,
                search: `?${searchParams.toString()}`,
              });
            }
          } else if (redirectedData && filteredData?.length > 0) {
            filteredData[0].is_read = true;
            handleSelectThread(filteredData[0]);

            if (filteredData[0]?.contact_id) {
              const searchParams = new URLSearchParams(location.search);
              searchParams.set('contactId', filteredData[0]?.contact_id);
              history.replace({
                pathname: location.pathname,
                search: `?${searchParams.toString()}`,
              });
            }

            removeLocalStorageItem('stateData');
          } else if (selectedContact) {
            // Find the thread that matches the selected contact
            const matchingThread = response.data.text_message_threads?.find(
              (thread) =>
                thread?.contact_id === selectedContact?.contact_id ||
                thread?.to === selectedContact?.phone ||
                thread?.from_ === selectedContact?.phone
            );
            if (matchingThread) {
              matchingThread.is_read = true;
              handleSelectThread(matchingThread);

              if (matchingThread?.contact_id) {
                const searchParams = new URLSearchParams(location.search);
                searchParams.set('contactId', matchingThread?.contact_id);
                history.replace({
                  pathname: location.pathname,
                  search: `?${searchParams.toString()}`,
                });
              }
            } else {
              // If no exact match found, create a thread object with selected contact's info
              // This ensures fetchMessageHistory is called with the correct contact_id and phone
              // Preserve sk, id, and is_active from current selectedThread if it matches this contact
              const preserveFromCurrent = selectedThread?.contact_id === selectedContact?.contact_id;
              const threadForContact = {
                client_name: { first_name: selectedContact?.first_name, last_name: selectedContact?.last_name },
                contact_id: selectedContact?.contact_id,
                to: selectedContact?.phone,
                phone: selectedContact?.phone,
                from_: userContext?.firmPhoneNumber,
                // Preserve is_active, sk, id from current thread if same contact (for tab switching)
                is_active: preserveFromCurrent ? selectedThread?.is_active : true,
                sk: preserveFromCurrent ? selectedThread?.sk : undefined,
                id: preserveFromCurrent ? selectedThread?.id : undefined,
                latest_phone_number: selectedContact?.phone,
              };
              threadForContact.is_read = true;
              handleSelectThread(threadForContact);

              if (threadForContact?.contact_id) {
                const searchParams = new URLSearchParams(location.search);
                searchParams.set('contactId', threadForContact?.contact_id);
                history.replace({
                  pathname: location.pathname,
                  search: `?${searchParams.toString()}`,
                });
              }
            }
          }
          setMessageThreads(response.data.text_message_threads);
        } else {
          if (selectedContact) {
            // Preserve sk, id, and is_active from current selectedThread if it matches this contact
            const preserveFromCurrent = selectedThread?.contact_id === selectedContact?.contact_id;
            selectedContact.is_read = true;
            handleSelectThread({
              client_name: { first_name: selectedContact?.first_name, last_name: selectedContact?.last_name },
              contact_id: selectedContact?.contact_id,
              to: selectedContact?.phone,
              phone: selectedContact?.phone,
              from_: userContext?.firmPhoneNumber,
              // Preserve is_active, sk, id from current thread if same contact (for tab switching)
              is_active: preserveFromCurrent ? selectedThread?.is_active : true,
              sk: preserveFromCurrent ? selectedThread?.sk : undefined,
              id: preserveFromCurrent ? selectedThread?.id : undefined,
              latest_phone_number: selectedContact?.phone,
            });

            if (selectedContact?.contact_id) {
              const searchParams = new URLSearchParams(location.search);
              searchParams.set('contactId', selectedContact?.contact_id);
              history.replace({
                pathname: location.pathname,
                search: `?${searchParams.toString()}`,
              });
            }
          }
          setMessageThreads([]);
          setIsLastPage(true);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const loadThreads = () => {
    // Stop if already loading
    if (isLoading) {
      return;
    }

    // Stop if we've reached the last page
    if (isLastPage === true) {
      return;
    }

    setIsLoading(true);

    // Build data object with appropriate filters
    let data = {};
    if (!isFromCase && !isFromLead) {
      // For global text messages, include practice area filter
      data = { practice_area_ids: selectedPracticeAreas };
    } else {
      // For case/lead specific text messages, include case_ids/lead_ids filter
      data = { practice_area_ids: [] };
      if (isFromCase && caseDetails?.case_id) {
        data.case_ids = [caseDetails.case_id];
      }
      if (isFromLead && leadDetails?.case_id) {
        data.case_ids = [leadDetails.case_id];
      }
    }

    // Add contact_ids to data object when a contact is selected
    if (selectedContact?.contact_id) {
      data.contact_ids = [selectedContact.contact_id];
    }

    const nextPage = pageCount;

    listTextMessages(
      leadDetails?.case_id || caseDetails?.case_id,
      selectedContact?.contact_id,
      false,
      dynamicLimit,
      activeIndex === 0 ? '' : false,
      null, // No more nextToken
      'forward',
      true,
      nextPage,
      data
    )
      .then((response) => {
        if (response?.data?.text_message_threads?.length) {
          setMessageThreads((prev) => {
            return [...prev, ...response.data.text_message_threads];
          });
          setIsLastPage(response?.data?.is_last_page || false);
          setPageCount(nextPage + 1);
        } else {
          setIsLastPage(true);
        }
      })
      .catch((error) => {
        addToast(
          false,
          toastConstant.toasterType.ERROR,
          toastConstant.api.FAILED,
          toastConstant.message.TEXT_MESSAGE_THREAD_NOT_FOUND_FAILED
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  function navigateToLatest(contact_id) {
    // Build data object with appropriate filters and contact_ids
    let data = {};
    if (!isFromCase && !isFromLead) {
      data = { practice_area_ids: selectedPracticeAreas };
    } else {
      data = { practice_area_ids: [] };
      if (isFromCase && caseDetails?.case_id) {
        data.case_ids = [caseDetails.case_id];
      }
      if (isFromLead && leadDetails?.case_id) {
        data.case_ids = [leadDetails.case_id];
      }
    }

    // Add contact_ids to the data object
    data.contact_ids = contact_id ? [contact_id] : [];

    listTextMessages(caseDetails?.case_id, contact_id, false, dynamicLimit, '', null, 'forward', false, 1, data)
      .then((response) => {
        if (response?.data?.text_message_threads?.length) {
          // Find thread has contact_status === 'ACTIVE'
          let latestThread = response?.data?.text_message_threads?.find((c) => c.contact_status === 'ACTIVE');
          latestThread.is_read = true;
          handleSelectThread(latestThread);

          if (latestThread?.contact_id) {
            const searchParams = new URLSearchParams(location.search);
            searchParams.set('contactId', latestThread?.contact_id);
            history.replace({
              pathname: location.pathname,
              search: `?${searchParams.toString()}`,
            });
          }
        } else {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.TEXT_MESSAGE_THREAD_NOT_FOUND);
        }
      })
      .catch((error) => {
        console.log(error);
        addToast(
          false,
          toastConstant.toasterType.ERROR,
          toastConstant.api.FAILED,
          toastConstant.message.TEXT_MESSAGE_THREAD_NOT_FOUND_FAILED
        );
      });
  }

  const handleSelectThread = (thread) => {
    thread.is_read = true;
    setSelectedThread(thread);

    if (thread?.contact_id) {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('contactId', thread?.contact_id);
      history.replace({
        pathname: location.pathname,
        search: `?${searchParams.toString()}`,
      });
    }
  };

  return {
    messageThreads,
    setMessageThreads,
    selectedThread,
    handleSelectThread,
    selectedContact,
    setSelectedContact,
    newThreadActive,
    setNewThreadActive,
    activeIndex,
    setActiveIndex,
    fetchTextMessages,
    loadThreads,
    navigateToLatest,
    isLoading,
    selectedPracticeAreas,
    setSelectedPracticeAreas,
    pageCount,
    setPageCount,
    isLastPage,
    setIsLastPage,
  };
}
